import { e, button } from "../dom";
import { icon } from "../icons";
import wizardIcon from "../assets/wizard-icon.svg?raw";
import { SPELL_COPY, SLOT_KEYS } from "../spells";
import { spellRank } from "../upgrades";
import {
  colorRoster,
  playerColorIndex,
  playerColorStyle,
} from "../player-colors";
import type { AppState, Player, Spell } from "../types";

export function avatar(
  id: number,
  small = false,
  roster: readonly number[] = [id],
): string {
  const color = playerColorIndex(id, roster);
  return `<span class="avatar ${small ? "mini" : ""} color-${color}" style="${playerColorStyle(color)}" aria-hidden="true">${wizardIcon}</span>`;
}
export function teamName(team: number | null | undefined): string {
  return `Team ${(team ?? 0) + 1}`;
}
export function teamMark(team: number | null | undefined): string {
  return team == null
    ? ""
    : `<span class="team-mark team-${team}" title="${teamName(team)}">${team % 2 === 0 ? "◆" : "▲"}</span>`;
}
export function toolbar(label = "Settings"): string {
  return `<div class="corner-settings">${button(label === "Settings" ? "settings" : "menu", `${icon("settings")}<span>${e(label)}</span>`, { small: true })}</div>`;
}
export function identity(state: AppState): string {
  return `<div class="identity beveled">${avatar(state.playerId, true, colorRoster(state))}<strong>${e(state.name || "Wandering wizard")}</strong><span class="divider"></span><button class="text-button" data-action="rename">${icon("edit")}Rename</button></div>`;
}
export function spellSlots(
  player: Player | undefined,
  selectable = false,
  selected = 1,
  reorderable = false,
  reorderPending = false,
): string {
  const loadout: (Spell | null)[] = player?.loadout ?? [
    "fireball",
    null,
    null,
    null,
  ];
  const slots = `<div class="spell-slots">${loadout
    .map((spell, i) => {
      const copy = spell ? SPELL_COPY[spell] : null;
      const rank = spell ? spellRank(player, spell) : 0;
      const inner = `${copy ? icon(copy.icon) : '<span class="empty-spell">—</span>'}${rank ? `<span class="slot-rank" aria-label="Upgrade rank ${rank}">${rank === 1 ? "I" : "II"}</span>` : ""}<span class="slot-name">${copy ? copy.name : "Empty slot"}</span><kbd>${SLOT_KEYS[i]}</kbd><span class="cooldown" data-cooldown="${i}"></span>`;
      if (reorderable || reorderPending) {
        const selection = selectable && !spell;
        return `<button type="button" class="spell-slot ${reorderable ? "reorderable" : ""} ${spell ? "occupied" : ""} ${selection ? "selectable" : ""} ${selection && selected === i ? "selected" : ""}" draggable="${reorderable && !!spell}" data-spell-slot="${i}" ${selection ? `data-action="select-slot" data-slot="${i}"` : ""} aria-disabled="${!reorderable}" aria-pressed="${selection && selected === i}" aria-label="${copy ? `${copy.name}, ${SLOT_KEYS[i]}. Move spell` : `Empty slot, ${SLOT_KEYS[i]}. Equip next spell here`}" aria-describedby="spell-reorder-help">${inner}</button>`;
      }
      return selectable && !spell
        ? `<button type="button" class="spell-slot selectable ${selected === i ? "selected" : ""}" data-action="select-slot" data-slot="${i}" aria-pressed="${selected === i}" aria-label="Equip next spell to ${SLOT_KEYS[i]}">${inner}</button>`
        : `<div class="spell-slot ${spell ? "occupied" : ""}" title="${copy?.detail ?? "Learn a spell between rounds"}">${inner}</div>`;
    })
    .join("")}</div>`;
  return (reorderable || reorderPending) && player
    ? `<div class="spell-reorder" data-spell-reorder data-player-id="${player.id}" data-loadout-revision="${player.loadout_revision ?? 0}" data-loadout="${e(JSON.stringify(loadout))}" role="group" aria-label="Arrange your spell slots" aria-busy="${reorderPending}">${slots}<p class="spell-reorder-hint" id="spell-reorder-help">${reorderPending ? "Updating spell slots…" : "Drag a spell to move or swap it. <span>Keyboard: Space or Enter to pick up, arrows to choose, Space or Enter to drop; Escape cancels.</span>"}</p></div>`
    : slots;
}
export function localPlayer(state: AppState): Player | undefined {
  return state.snapshot?.players.find((p) => p.id === state.matchPlayerId);
}
export function viewedPlayer(state: AppState): Player | undefined {
  const own = localPlayer(state);
  return own?.alive
    ? own
    : (state.snapshot?.players.find((p) => p.id === state.followedPlayerId) ??
        own);
}
export function gold(value: number, large = false): string {
  return `<span class="gold ${large ? "large" : ""}">${icon("coin")}<b data-wallet>${value}</b><span>gold</span></span>`;
}
